/**
 * Single source of truth for how dates are DISPLAYED across the system.
 *
 *   Display format:  Feb 02, 1984
 *   With time:       Feb 02, 1984 01:45 PM
 *
 * Use these only for what the user SEES (tables, labels, print/PDF headers).
 * Values sent to the API should stay in ISO form - use toApiDate()/toApiDateRange().
 *
 * Dependency-free on purpose: no timezone surprises from parsing "YYYY-MM-DD"
 * as UTC, and it accepts strings, Date objects, dayjs/moment objects and numbers.
 */

export const DATE_FORMAT = "MMM DD, YYYY"; // dayjs/moment/antd token form of "Feb 02, 1984"
export const DATE_TIME_FORMAT = "MMM DD, YYYY hh:mm A";
export const TIME_FORMAT = "hh:mm A";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const pad = (n) => String(n).padStart(2, "0");

// "1984-02-02", "1984-02-02 13:45:00", "1984-02-02T13:45:00.000Z", "1984-02-02T13:45:00+08:00"
const ISO_LIKE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i;

/**
 * Turn almost anything date-like into a valid JS Date, or null.
 * Date-only strings are treated as LOCAL dates (so Feb 02 never becomes Feb 01).
 * Strings with Z / an offset are converted to local time.
 */
export const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;

  // dayjs / moment objects
  if (typeof value === "object" && typeof value.toDate === "function") {
    value = value.toDate();
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === "string") {
    const str = value.trim();
    if (!str || str.startsWith("0000-00-00")) return null; // MySQL "empty" dates

    const m = ISO_LIKE.exec(str);
    if (m) {
      const [, y, mo, d, h, mi, s, tz] = m;
      let date;
      if (tz) {
        date = new Date(str.replace(" ", "T"));
      } else {
        date = new Date(+y, +mo - 1, +d, +(h || 0), +(mi || 0), +(s || 0));
        // reject overflow such as 2023-02-31
        if (date.getFullYear() !== +y || date.getMonth() !== +mo - 1 || date.getDate() !== +d) {
          return null;
        }
      }
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const fallback = new Date(str);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  return null;
};

const dateText = (d) => `${MONTHS[d.getMonth()]} ${pad(d.getDate())}, ${d.getFullYear()}`;

const timeText = (d) => {
  const h = d.getHours();
  return `${pad(h % 12 === 0 ? 12 : h % 12)}:${pad(d.getMinutes())} ${h < 12 ? "AM" : "PM"}`;
};

/** Feb 02, 1984 */
export const formatDate = (value, fallback = "") => {
  const d = toDate(value);
  return d ? dateText(d) : fallback;
};

/** Feb 02, 1984 01:45 PM */
export const formatDateTime = (value, fallback = "") => {
  const d = toDate(value);
  return d ? `${dateText(d)} ${timeText(d)}` : fallback;
};

/** 01:45 PM  (accepts a full date-time or a bare "HH:mm[:ss]" string) */
export const formatTime = (value, fallback = "") => {
  if (typeof value === "string") {
    const t = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
    if (t) {
      const h = +t[1];
      return `${pad(h % 12 === 0 ? 12 : h % 12)}:${t[2]} ${h < 12 ? "AM" : "PM"}`;
    }
  }
  const d = toDate(value);
  return d ? timeText(d) : fallback;
};

/** Feb 02, 1984 - Feb 10, 1984 */
export const formatDateRange = (from, to, fallback = "") => {
  const a = formatDate(from);
  const b = formatDate(to);
  if (a && b) return `${a} - ${b}`;
  return a || b || fallback;
};

/* ------------------------------------------------------------------ */
/* API helpers - keep what is SENT to the backend in ISO (YYYY-MM-DD)  */
/* ------------------------------------------------------------------ */

/** "1984-02-02" (or "" when empty/invalid). Safe to call with a dayjs object or a string. */
export const toApiDate = (value) => {
  const d = toDate(value);
  return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "";
};

/** [from, to] in ISO form, e.g. from an antd RangePicker onChange(dates). Cleared -> ["", ""]. */
export const toApiDateRange = (dates) =>
  Array.isArray(dates) && dates.length === 2
    ? [toApiDate(dates[0]), toApiDate(dates[1])]
    : ["", ""];

/* ------------------------------------------------------------------ */
/* Safety net for generic tables (headerSelector-driven)               */
/* ------------------------------------------------------------------ */

const DATE_KEY = /(^|_)(date|dates|birthdate|birth_date|dob|on|at)$|_date_|date_(from|to)$/i;
const STRICT_ISO = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?\s*(?:Z|[+-]\d{2}:?\d{2})?)?$/i;

/**
 * For columns whose key looks like a date (birthdate, added_on, result_date, ...),
 * turns a RAW ISO value into display text. Returns null when the cell should be left alone
 * (not a date-like key, or the value is already formatted / not ISO).
 */
export const autoFormatDateCell = (key, value) => {
  if (typeof value !== "string" || !key || !DATE_KEY.test(String(key))) return null;
  const v = value.trim();
  if (!STRICT_ISO.test(v)) return null;
  const hasTime = /[T ]\d{2}:\d{2}/.test(v) && !/[T ]00:00(?::00)?(?:\.0+)?\s*(?:Z|[+-]\d{2}:?\d{2})?$/i.test(v);
  const out = hasTime ? formatDateTime(v) : formatDate(v);
  return out || null;
};
